import React, { useRef } from 'react';
import { Modal, Typography, Button, Box, Stack } from '@mui/material';
import {
    FacebookShareButton,
    RedditShareButton,
    TwitterShareButton,
    EmailShareButton,
    TelegramShareButton,
    EmailIcon,
    FacebookIcon,
    RedditIcon,
    TelegramIcon,
    TwitterIcon,
    WhatsappShareButton,
    WhatsappIcon,
} from 'react-share';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { CustomTextField } from '../TextInput/CustomTextField';
import closeIcon from '../../asset/images/icons/es_x_white.svg';
import { DraggableBottomSheetModal } from '../Draggable/DraggableBottomSheet';
import { showToastMessage } from '../../utils/AppUtils';
import CloseIcon from '../CustomSVGIcons/CloseIcon';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';

export interface IShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    shareUrl: string;
    title: string;
}

const ICON_SIZE = 50;

const STOP = false;

const ShareModal: React.FC<IShareModalProps> = ({ isOpen, onClose, shareUrl, title }) => {
    const scrollContainerRef: any = useRef(null);
    const { themeStyle, device } = useThemeContext();
    const shareBox = (
        <Box
            sx={{
                position: 'absolute',
                top: '40%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '100%',
                borderRadius: 4,
                zIndex: 9999,
            }}>
            <Stack
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                borderBottom={1}
                mb={2}
                p={2}
                borderColor={theme[themeStyle].background_gray}>
                <Typography
                    style={{
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        color: theme[themeStyle].tag_white,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                    }}>
                    {title}
                </Typography>
                <CloseIcon onClick={onClose} />
            </Stack>
            <Box style={{ alignItems: 'center' }} sx={{ px: 3 }}>
                <div
                    ref={scrollContainerRef}
                    style={{
                        overflowX: 'scroll',
                        whiteSpace: 'nowrap',
                        width: '100%',
                    }}>
                    <Stack flexDirection="row" justifyContent="space-evenly">
                        <Box sx={{ mr: 1 }}>
                            <WhatsappShareButton url={shareUrl} title={title}>
                                <WhatsappIcon size={ICON_SIZE} round={true} />
                            </WhatsappShareButton>
                        </Box>
                        <Box sx={{ mr: 1 }}>
                            <FacebookShareButton url={shareUrl} quote={title}>
                                <FacebookIcon size={ICON_SIZE} round={true} />
                            </FacebookShareButton>
                        </Box>
                        <Box sx={{ mr: 1 }}>
                            <RedditShareButton url={shareUrl} title={title}>
                                <RedditIcon size={ICON_SIZE} round={true} />
                            </RedditShareButton>
                        </Box>
                        <Box sx={{ mr: 1 }}>
                            <TwitterShareButton url={shareUrl} title={title}>
                                <TwitterIcon size={ICON_SIZE} round={true} />
                            </TwitterShareButton>
                        </Box>
                        <Box sx={{ mr: 1 }}>
                            <EmailShareButton url={shareUrl} title={title}>
                                <EmailIcon size={ICON_SIZE} round={true} />
                            </EmailShareButton>
                        </Box>
                        <Box sx={{ mr: 1 }}>
                            <TelegramShareButton url={shareUrl}>
                                <TelegramIcon size={ICON_SIZE} round={true} />
                            </TelegramShareButton>
                        </Box>
                    </Stack>
                </div>
                <Box sx={{ mt: 3 }}>
                    <CustomTextField
                        enableCustomButton
                        customButton={
                            <Button
                                onClick={() => {
                                    navigator.clipboard.writeText(shareUrl);
                                    showToastMessage('Link copied successfully', 'success');
                                    onClose && onClose();
                                }}>
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                                        fontSize: 10,
                                        color: theme[themeStyle].black,
                                        backgroundColor: theme[themeStyle].highlight,
                                        padding: 10,
                                        borderRadius: 20,
                                    }}>
                                    Copy
                                </Typography>
                            </Button>
                        }
                        textFieldProps={{
                            InputProps: {
                                disabled: false,
                                disableUnderline: true,
                                value: shareUrl,
                                style: {
                                    fontSize: 12,
                                    fontFamily: FONT_ENUM.REGULAR_HEADER,
                                    color: theme[themeStyle].black,
                                    backgroundColor: theme[themeStyle].darkGray60,
                                },
                            },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );

    const mobileShare = (
        <DraggableBottomSheetModal
            open={isOpen}
            hideCloseIcon
            customHeight="30%"
            onDismiss={onClose}
            style={{
                position: 'fixed',
                bottom: -10,
            }}>
            {shareBox}
        </DraggableBottomSheetModal>
    );

    const desktopShare = (
        <Modal open={isOpen} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: {
                        xs: '80vw',
                        sm: 450,
                    },
                    bgcolor: 'white',
                    padding: 3,
                    borderRadius: 4,
                    backgroundColor: theme[themeStyle].deep_grey,
                }}>
                <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
                    <Typography
                        style={{
                            fontFamily: FONT_ENUM.REGULAR_HEADER,
                            color: theme[themeStyle].tag_white,
                            marginBottom: 20,
                        }}>
                        {title}
                    </Typography>
                    <CustomLazyLoadImage
                        src={closeIcon}
                        alt="Close icon"
                        style={{ height: '30px', width: '30px', marginBottom: '10px', cursor: 'pointer' }}
                        onClick={onClose}
                    />
                </Stack>
                <Box style={{ alignItems: 'center' }}>
                    <div
                        ref={scrollContainerRef}
                        style={{
                            overflowX: 'scroll',
                            whiteSpace: 'nowrap',
                            width: '100%',
                        }}>
                        <Stack flexDirection="row" justifyContent="space-evenly">
                            <Box sx={{ mr: 1 }}>
                                <WhatsappShareButton url={shareUrl} title={title}>
                                    <WhatsappIcon size={ICON_SIZE} round={true} />
                                </WhatsappShareButton>
                            </Box>
                            <Box sx={{ mr: 1 }}>
                                <FacebookShareButton url={shareUrl} quote={title}>
                                    <FacebookIcon size={ICON_SIZE} round={true} />
                                </FacebookShareButton>
                            </Box>
                            <Box sx={{ mr: 1 }}>
                                <RedditShareButton url={shareUrl} title={title}>
                                    <RedditIcon size={ICON_SIZE} round={true} />
                                </RedditShareButton>
                            </Box>
                            <Box sx={{ mr: 1 }}>
                                <TwitterShareButton url={shareUrl} title={title}>
                                    <TwitterIcon size={ICON_SIZE} round={true} />
                                </TwitterShareButton>
                            </Box>
                            <Box sx={{ mr: 1 }}>
                                <EmailShareButton url={shareUrl} title={title}>
                                    <EmailIcon size={ICON_SIZE} round={true} />
                                </EmailShareButton>
                            </Box>
                            <Box sx={{ mr: 1 }}>
                                <TelegramShareButton url={shareUrl}>
                                    <TelegramIcon size={ICON_SIZE} round={true} />
                                </TelegramShareButton>
                            </Box>
                        </Stack>
                    </div>
                    <Box sx={{ mt: 3 }}>
                        <CustomTextField
                            enableCustomButton
                            customButton={
                                <Button
                                    onClick={() => {
                                        navigator.clipboard.writeText(shareUrl);
                                        showToastMessage('Link copied successfully', 'success');
                                        onClose && onClose();
                                    }}>
                                    <Typography
                                        style={{
                                            fontFamily: FONT_ENUM.REGULAR_HEADER,
                                            fontSize: 10,
                                            color: theme[themeStyle].black,
                                            backgroundColor: theme[themeStyle].highlight,
                                            padding: 10,
                                            borderRadius: 20,
                                        }}>
                                        Copy
                                    </Typography>
                                </Button>
                            }
                            textFieldProps={{
                                InputProps: {
                                    disabled: false,
                                    disableUnderline: true,
                                    value: shareUrl,
                                    style: {
                                        fontSize: 12,
                                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                                        color: theme[themeStyle].black,
                                        backgroundColor: theme[themeStyle].darkGray60,
                                    },
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        </Modal>
    );

    return device.isMobile && !STOP ? mobileShare : desktopShare;
};

export { ShareModal };
